import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { PlusIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { useFieldError } from '../builder/useFieldError';
import { useWorkflows } from '../hooks/useWorkflows';
import { CreateWorkflowDialog } from './CreateWorkflowDialog';

/**
 * The item that creates instead of choosing. Not a key a workflow can have:
 * keys are slugs of names, and a slug never starts with an underscore.
 */
const NEW_WORKFLOW = '__new__';

/**
 * A `linked_workflow` step's target (spec 101 US1), and where its child is born
 * (spec 202).
 *
 * "New workflow…" comes first because building a parent top-down is the point:
 * the child is created as an empty draft and linked at once, and the person
 * stays on the parent with their edits. The service lets a draft link to
 * drafts and refuses to publish until every linked workflow is published
 * (service spec 161), so a half-built child cannot run by accident.
 *
 * Drafts say so, here and under the field, with the way to go and finish one.
 * Drafts only reach the sidebar when pinned (spec 174), so without this link
 * the child would be easy to lose.
 *
 * The workflow being edited stays in the list: a workflow may link to itself
 * (specs 097, 108).
 */
export function SubworkflowPicker({
  workspaceId,
  value,
  onCommit,
}: {
  workspaceId: string;
  value: string | null;
  onCommit: (key: string) => void;
}) {
  const { workflows, isPending, loadFailed } = useWorkflows(workspaceId);
  const { error } = useFieldError('workflow');
  const [creating, setCreating] = useState(false);

  if (isPending) {
    return (
      <div className="flex flex-col gap-1.5">
        <Label>Target workflow</Label>
        <Select value="" disabled>
          <SelectTrigger aria-label="Target workflow">
            <SelectValue>{() => 'Loading…'}</SelectValue>
          </SelectTrigger>
          <SelectContent />
        </Select>
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="flex flex-col gap-1.5">
        <Label>Target workflow</Label>
        <p role="alert" className="text-destructive text-xs">
          The workspace&apos;s workflows could not be loaded. Reload to try
          again.
        </p>
      </div>
    );
  }

  const target = value
    ? workflows.find((workflow) => workflow.key === value)
    : undefined;
  const isBroken = value != null && target === undefined;
  const isDraft = target?.status === 'draft';

  return (
    <div className="flex flex-col gap-1.5">
      <Label>Target workflow</Label>
      <Select
        value={value ?? ''}
        onValueChange={(next) => {
          if (next === NEW_WORKFLOW) {
            setCreating(true);
            return;
          }
          if (next) onCommit(next);
        }}
      >
        <SelectTrigger
          aria-label="Target workflow"
          aria-invalid={error ? true : undefined}
        >
          <SelectValue>
            {(key: string) => {
              if (!key) return 'Choose a workflow';
              return (
                workflows.find((workflow) => workflow.key === key)?.name ??
                'Unknown workflow'
              );
            }}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {/* An action, not a workflow (spec 210, the review's P10): the plus
              and the rule below set it apart from the list it adds to. */}
          <SelectItem value={NEW_WORKFLOW}>
            {/* One line: an option's text box is a block, and so is a bare
                icon, which would stack the plus above the words. */}
            <span className="inline-flex items-center gap-1.5">
              <PlusIcon aria-hidden="true" className="size-4 shrink-0" />
              New workflow…
            </span>
          </SelectItem>
          {workflows.length > 0 && <SelectSeparator />}
          {workflows.map((workflow) => (
            <SelectItem key={workflow.key} value={workflow.key}>
              {workflow.name}
              {workflow.status === 'draft' && (
                <Badge variant="outline" className="ml-1.5">
                  Draft
                </Badge>
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {isDraft && target && (
        <div className="flex items-center gap-2 text-xs">
          <Badge variant="outline">Draft</Badge>
          {/* Said once: when a refused publish already says it under the
              field, this line would only repeat it. */}
          {!error && (
            <span className="text-muted-foreground">
              Publish it before this workflow can be published.
            </span>
          )}
          <Link
            to="/workspaces/$workspaceId/workflows/$workflowKey/builder"
            params={{ workspaceId, workflowKey: target.key }}
            className="text-primary ml-auto shrink-0 whitespace-nowrap underline-offset-4 hover:underline"
          >
            Open draft
          </Link>
        </div>
      )}
      {isBroken && (
        <p role="alert" className="text-destructive text-xs">
          The workflow this step targets no longer exists. Choose a valid
          workflow before this step can be saved.
        </p>
      )}
      {error && (
        <p role="alert" className="text-destructive text-xs">
          {error}
        </p>
      )}

      <CreateWorkflowDialog
        workspaceId={workspaceId}
        open={creating}
        onOpenChange={setCreating}
        description="It starts as an empty draft that this step links to. You stay here; build and publish it when you are ready."
        onCreated={(workflow) => onCommit(workflow.key)}
      />
    </div>
  );
}
